"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type ComponentProps,
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { cardAt, ownHref, overHref } from "@/components/shell/cards";
import { OnTop } from "@/components/shell/OnTop";
import { useRestored } from "@/components/shell/restored";
import { t, type Lang } from "@/lib/i18n/index";
import { record } from "./record";

const SHOW_AFTER_MS = 150;
/**
 * How long a lock is held for something the shell cannot see end: a hard
 * navigation (it is over when this document is gone), a way back through the
 * history, a write's request. Past it the page is let go unasked, so nothing
 * that never answers can leave the sheet over it for good.
 */
const RELEASE_AFTER_MS = 8_000;
/** When a navigation the shell follows (`go`) says it is slower than usual. */
const SLOW_AFTER_MS = 8_000;
/**
 * The timeout a followed navigation's request does not have (Issue 697). The
 * App Router's request for the next page has none — not the browser's, not
 * Next's, and `router.push` takes none — and a page compiled on first visit
 * in development is 20–40 seconds of nothing wrong. Everything that does
 * answer decides by its answer: a server-side timeout, a 5xx or a dropped
 * connection comes back as a failed response, and Next turns that into a
 * full page load, which takes this page and its sheet away with it. So this
 * is only for no answer at all, and it is longer than a connection and a
 * query's own timeouts (`lib/db/pool.ts`), so a server that gives up on one
 * says so first. (A page's queries run one after another can together run
 * past it; the server promises nothing for the whole of a render.)
 */
export const GIVE_UP_AFTER_MS = 60_000;

type Claim = "started" | "duplicate" | "same-location" | "not-local";
type ReleaseReason = "arrived" | "failed" | "offline" | "replaced" | "released";
type Move = "card" | "navigation";
/** What a claim is for: a move (a card, or a navigation), or a write — one
 *  that redraws the page it is on (`usePendingRefresh`, `usePendingWrites`).
 *  Said by every caller: which it is decides what a move within the page may
 *  do beside it, and whether that move arriving lets it go (Issue 713). */
type Holder = Move | "write";
/** What a move within the page came to (`moveIn`): on its way, refused
 *  (nothing to do, or an area it redraws is already waiting), or not one
 *  the page can draw in its areas — the caller takes the lock instead. */
type AreaMoveClaim = "started" | "refused" | "not-area";
/** What showing an address the page already has came to (`showHere`): shown
 *  — the address is it now, or already was — or not now, because the shell
 *  has something on its way, or not this page at all. Either of those is the
 *  caller's to make an ordinary move of. */
type ShowHere = "shown" | "busy" | "not-here";
type Controller = Readonly<{
  lang: Lang;
  /** A move that only changes this page's address — its query — and redraws
   *  the named areas of it (`LoadingArea`, Issue 713). It does not take the
   *  lock; see the provider's area moves. */
  moveIn: (href: string, areas: readonly string[], replace?: boolean) => AreaMoveClaim;
  /** A move within the page that the page draws from what it already has
   *  (Issue 766): only the address changes, and nothing is asked of the
   *  server. See the provider's `showHere`. */
  showHere: (href: string) => ShowHere;
  /** A router call nobody waits on — a redraw (`redraw`), a write's late
   *  answer past its lock — made in the provider's own transition, so it is
   *  counted out until it commits wherever it was asked from, the asker
   *  unmounted or not (Issue 766). A refusal is logged and dropped: there is
   *  nobody to tell. */
  aside: (what: string, call: () => void) => void;
  /** The page drawn again from the server (`useRefresh`) — now, or, while a
   *  move is out, once it is over (Issue 819). See the provider's `redraw`. */
  redraw: () => void;
  /** The address this page is on its way to: a move within it still out,
   *  or where it is. What a card is opened over (`softTarget`). */
  address: () => string;
  claim: (href: string | null, reload: boolean, quiet: boolean, holder: Holder) => Claim;
  /** Whether that address is the one being shown, by the same reckoning the
   *  release below uses. A caller comparing anything else — the step it drew,
   *  say — is comparing something the address is allowed to disagree with. */
  isHere: (href: string | null) => boolean;
  /** Let go, whoever holds it. It is also called by no holder at all — the
   *  fallback or a followed navigation's timeout, and the page coming back
   *  from the back-forward cache (the provider's `pageshow`) — so every
   *  holder here takes being let go at any moment: what it was waiting for
   *  may still answer later, and that answer must not touch a lock taken
   *  since (`claimRef`, `owner.active`, `lost`). */
  release: () => void;
  /** Push the fallback release back to a full `RELEASE_AFTER_MS` from now.
   *  Only the lock's holder calls it — `usePendingWrites`, as each write
   *  joins a lock it already holds: the fallback is for a lock nobody is
   *  using any more, and one still taking writes is being used. Called on a
   *  lock following a navigation (`go`), it would put the eight seconds back
   *  in place of the navigation's own end; no holder can reach one now. */
  renew: () => void;
  onRelease: (listener: (reason: ReleaseReason) => void) => () => void;
  /** Make the soft navigation the lock was just claimed for (`claim` first).
   *  Ordinary moves let go when the address changes or the transition commits
   *  (a redirect may end at the address already shown). Card moves wait for
   *  both their `?card=` address and the transition's RSC commit: Next may
   *  write the URL before it has drawn the sheet. `href` is a local path.
   *
   *  From here the lock follows the navigation, not the fallback (Issue
   *  697): a slow page is waited for, said to be slow at `SLOW_AFTER_MS`, and
   *  given up on — with a message saying so — only at `GIVE_UP_AFTER_MS`. */
  go: (href: string, opts?: { replace?: boolean; scroll?: boolean }) => void;
  /** Go `delta` entries through the browser's history for the lock just
   *  claimed (`claim(<that entry's address>)` first), to an entry of this
   *  document (`shell/history.ts`). Arriving does not let go: the App Router
   *  brings a traversed-to page back from its cache, as it was when it was
   *  left, so the page is refreshed first, in the same lock, and the lock is
   *  let go when that commits (Issue 557) — what was done meanwhile, such as
   *  a rewrite begun on a card, shows on the card they come back to. A card
   *  over a page is refreshed like any page: it is the page's `?card=`
   *  (`shell/cards.ts`, Issue 567). */
  traverse: (delta: number) => void;
  /** A write answered by a move rather than a refresh (a pending task's
   *  `moveTo`): the pages behind in the browser's history were drawn before
   *  it, and the App Router brings them back from its cache as they were.
   *  The next time the browser's own back or forward lands, the page is
   *  refreshed first, in the lock, as a `traverse` is (Issue 742). */
  movedAfterWrite: () => void;
  /** A refresh went out: it drops every cached page (the App Router's
   *  refresh invalidates its back-forward cache too), so nothing behind in
   *  history predates a write any more. */
  refreshed: () => void;
}>;
const NavigationPendingContext = createContext<Controller | null>(null);

type Notice = {
  reason: "failed" | "offline";
  /** The address it was said on: gone once they are on another. */
  at: string | null;
  /** The area that says it (a move within the page), or null for the page. */
  area: string | null;
};
/** What the areas of a page draw (`useLoadingArea`). Its own context, so the
 *  controller above is not a new object every time a wait changes. */
type AreaWait = Readonly<{
  lang: Lang;
  waiting: Readonly<{ areas: readonly string[]; visible: boolean; slow: boolean }> | null;
  notice: Notice | null;
  register: (area: string) => () => void;
}>;
const AreaWaitContext = createContext<AreaWait | null>(null);

function locationKey(pathname: string, search: string): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return `${path}?${search}`;
}

/**
 * Where a soft move to `href` goes. A card's own address is drawn over the
 * page they are on (`shell/cards.ts`), except from that card's own page,
 * where it is already all there is; anything else is itself. Absolute, like
 * the anchor's `href` it is usually read from.
 *
 * `over` is the page's address to open it over: where the page is going
 * when a move within it is still out (`address`), so the card does not take
 * the calendar back a month (Issue 713).
 */
function softTarget(href: string, over = window.location.href): { href: string; card: boolean } {
  const url = new URL(href, window.location.href);
  const card = url.origin === window.location.origin ? cardAt(url.pathname) : null;
  const here = cardAt(window.location.pathname);
  if (!card || (here && here.kind === card.kind && here.id === card.id)) return { href: url.href, card: false };
  return { href: new URL(overHref(over, card), window.location.href).href, card: true };
}

/**
 * A second move within the page while the first is out: where the first is
 * going, with what the second changes. The second link was drawn on the page
 * they are on, so what it changes is every parameter it spells differently
 * from the address shown — the rest of it is the page as it was, and taking
 * that too would undo the first move (a month turned, then a row picked:
 * both). The two change different parameters: they redraw different areas,
 * or the second is refused (`moveIn`).
 */
function composed(pending: string, link: string): string {
  const here = new URL(window.location.href);
  const to = new URL(pending, here);
  const want = new URL(link, here);
  const names = new Set([...here.searchParams.keys(), ...want.searchParams.keys()]);
  for (const name of names) {
    const was = here.searchParams.getAll(name);
    const now = want.searchParams.getAll(name);
    if (was.length === now.length && was.every((v, i) => v === now[i])) continue;
    to.searchParams.delete(name);
    for (const v of now) to.searchParams.append(name, v);
  }
  to.hash = want.hash;
  return local(to.href);
}

/** A same-origin absolute address as the path the router is given. */
function local(href: string): string {
  const url = new URL(href);
  return url.pathname + url.search + url.hash;
}

function targetKey(href: string | null): string | null {
  if (!href || typeof window === "undefined") return null;
  const url = new URL(href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  return locationKey(url.pathname, url.searchParams.toString());
}

/**
 * The one way this shell hands the router a call in a transition — a
 * navigation, a move within the page, a refresh — whose pending state is the
 * signal it is waiting on: the App Router holds the transition until the page
 * it asked for commits. `send(what, call)` makes the call in the transition
 * and answers whether the router took it. One it refused, by throwing, is
 * logged here; what the caller does about it, it does after `send` returns,
 * outside the transition (Issue 718):
 *
 *   - state set inside it waits on its lane, and a navigation given up on is
 *     still out on this same transition (Next does not cancel it): React
 *     binds the two, and the lock let go, the message said, commit only when
 *     that one does — which may be never;
 *   - thrown from it, React keeps the error for the next render and throws it
 *     at the nearest error boundary: it never reaches a `try` around the
 *     start, and the page is swapped for an error over a call that simply did
 *     not start.
 *
 * Nothing else in the app starts a transition (`navigation-surfaces.test`).
 *
 * And every one it sends is counted out until its transition ends — in the
 * provider's `out` (`RouterOutContext`, or the provider's own, handed in):
 * the App Router's queue says nothing of what it holds, and an address
 * written while a call is in it drops that call (`showHere`, Issue 766). The
 * transition ending is the signal, not the lock: a lock is let go at its
 * fallback while the refresh it covered is still on its way.
 */
function useRouterTransition(own?: Set<object>): readonly [pending: boolean, send: (what: string, call: () => void) => boolean] {
  const [pending, start] = useTransition();
  const tree = useContext(RouterOutContext);
  const out = own ?? tree;
  // This hook's one entry: it is out from a call sent until its transition
  // has ended — all the calls sent on it, as React holds them as one.
  const [mine] = useState(() => ({}));
  // How many calls this hook has sent, and how many it had when it drew the
  // `pending` its effect reads. A call sent between a drawing that saw the
  // transition over and that drawing's effect — another component's effect
  // in the same commit, sending through the provider's `aside` — is out,
  // though the effect reads `pending` false: it is not the effect's to
  // uncount (review of Issue 766).
  const sent = useRef(0);
  const drawnAt = sent.current;
  useEffect(() => {
    if (!pending && sent.current === drawnAt) out?.delete(mine);
  }, [pending, out, mine, drawnAt]);
  // Unmounted with a call still out — a card's write whose sheet closed on
  // it: the call goes on in the router's queue, where this hook can no longer
  // see it end. It stays counted as long as a followed navigation is waited
  // for (`GIVE_UP_AFTER_MS`), the one wait the shell has for an answer it
  // cannot see. (What is sent with nobody waiting goes through the provider's
  // own transition, which is never unmounted: `aside`.)
  useEffect(() => () => {
    if (out?.has(mine)) setTimeout(() => out.delete(mine), GIVE_UP_AFTER_MS);
  }, [out, mine]);
  const send = useCallback((what: string, call: () => void) => {
    // Cast, not annotated: set in the callback, where narrowing cannot see.
    let refused = null as { error: unknown } | null;
    start(() => {
      try {
        call();
      } catch (error) {
        refused = { error };
      }
    });
    if (refused === null) {
      sent.current++;
      out?.add(mine);
      return true;
    }
    console.error("[navigation] the router refused", what, refused.error);
    return false;
  }, [out, mine]);
  return [pending, send] as const;
}

/** The shell's router calls still out (`useRouterTransition`). */
const RouterOutContext = createContext<Set<object> | null>(null);

/**
 * The page drawn again from the server — `router.refresh()` — sent through
 * the shell (`redraw`, then `aside`), so that it is counted out until it
 * commits, whether or not whoever asked is still on the page (Issue 766): an
 * address the page already has is not written while it is on its way, which
 * would drop it (`showHere`). And not while a move is out: it goes once the
 * move is over (Issue 819). Like `router.refresh()`, it is sent and not
 * waited on; a caller that waits holds the lock instead
 * (`usePendingRefresh`). The one way the app asks for a redraw outside the
 * shell's own moves (`navigation-surfaces.test`).
 */
export function useRefresh(): () => void {
  const controller = useContext(NavigationPendingContext);
  const router = useRouter();
  return useCallback(() => {
    if (controller) controller.redraw();
    else router.refresh();
  }, [controller, router]);
}

/**
 * Owns the shell's one in-flight navigation. The ref is the lock: unlike
 * React state it changes inside the first Link's event, so a second click in
 * the same turn cannot queue another route before React paints the blocker.
 */
export function NavigationPendingProvider({ children, lang }: { children: ReactNode; lang: Lang }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentLocation = locationKey(pathname, searchParams.toString());
  const currentLocationRef = useRef(currentLocation);
  currentLocationRef.current = currentLocation;
  const pendingRef = useRef(false);
  const startedLocationRef = useRef<string | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The navigation the lock follows (`go`), and since when: the fallback is
  // not this lock's — the navigation's own end is.
  const followingRef = useRef<{ href: string; since: number } | null>(null);
  const quietRef = useRef(false);
  const releaseListenersRef = useRef(new Set<(reason: ReleaseReason) => void>());
  // Only a card navigation may be replaced by another card navigation. Keep
  // its destination separate from `startedLocationRef`: the URL can change
  // before the card's RSC payload has committed.
  const cardTargetRef = useRef<string | null>(null);
  // Which claim is the lock's now (one number per claim), and which claim's
  // navigation is in flight: a navigation that commits after its lock was
  // let go, and another taken, must not let go of the other one.
  const claimRef = useRef(0);
  const navigatingForRef = useRef<number | null>(null);
  // Which claim's traverse refreshes where it arrives (`traverse`).
  const arrivingForRef = useRef<number | null>(null);
  // Whether a write since the last refresh was answered by a move, leaving
  // pages behind in history that were drawn before it (`movedAfterWrite`).
  const behindStaleRef = useRef(false);
  // The router calls this shell has sent and not seen end (`useRouterTransition`).
  const [routerOut] = useState(() => new Set<object>());
  const [navigating, sendNavigation] = useRouterTransition(routerOut);
  const [pending, setPending] = useState(false);
  const [visible, setVisible] = useState(false);
  // A claim that holds the lock without drawing over the page (#397).
  //
  // The sheet is not only a picture: it carries `pointer-events: auto` from
  // the moment it is rendered, so it is also what stops a press elsewhere on
  // the page from reaching a surface that would then refuse it in silence.
  // A quiet claim gives that up, and is therefore only for a caller that
  // draws the wait itself *and* answers for the controls beside it:
  // onboarding's résumé step, where the drop names the file and the one
  // other button on the step disables itself; and the pages whose blocks
  // say so in markup (`data-wait`), where `shell/wait.ts` draws the wait on
  // the block and marks the Commander's own controls the lock would refuse
  // (Issue 534); and a card-opening link, which marks itself busy while a
  // different card link may replace it (Issue 638). What is not the
  // Commander's — a link, the upload on the same home page — is refused as
  // it always was for the second or so a block's write is out.
  const [quiet, setQuiet] = useState(false);
  // A followed navigation past `SLOW_AFTER_MS`: the sheet says so.
  const [slow, setSlow] = useState(false);
  // A navigation under the sheet that did not open, said on the page it left
  // them on — and only there: `at` is that page's address, so an arrival
  // after all (Next does not cancel a navigation given up on) takes it away.
  // A quiet claim says its own failure where it drew its wait (a card's link),
  // and a move within the page in the area it was drawn in (`area`).
  const [notice, setNotice] = useState<Notice | null>(null);
  const shownNotice = notice && notice.at === currentLocation ? notice : null;
  // Whether the lock is held by a write (`Holder`) rather than a move.
  const writeRef = useRef(false);

  // Moves within the page (Issue 713). A link that only changes this page's
  // query, and says which areas of the page that redraws (`redraws`), does
  // not take the lock: the areas draw its wait (`LoadingArea`), and the rest
  // of the page stays theirs — another area's link, a write on the card
  // beside it, a way off the page. What still cannot happen at once:
  //
  //   - two moves waiting on one area: the second is refused (a month
  //     turned twice before the first arrives is turned once);
  //   - a move within the page while a navigation holds the lock: there is
  //     one address, and it is being changed — unless the navigation is a
  //     card opening, which any move may replace (`claim`), this one too;
  //   - a move within the page while the sheet is over it: a write that is
  //     not quiet has the page, and a key pressed under the sheet is refused
  //     as a pointer is.
  //
  // A second move on other areas joins the first (`composed`): the App
  // Router makes only the last navigation it was given, so the second is
  // sent carrying the first's changes. A navigation that takes the lock
  // (`claim` with a move, `go`) makes the page's move its own: the move is
  // dropped, its areas stop waiting, and the address it was going to is
  // what a card is opened over (`address`). A write goes on beside one —
  // the App Router runs its refresh after the navigation, on the page it
  // arrives at — and the move arriving is not the write's page going away
  // (the address effect below).
  const areaMoveRef = useRef<{ href: string; target: string | null; areas: readonly string[] } | null>(null);
  const areaTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const areasOnPageRef = useRef(new Map<string, number>());
  // The move whose transition is out: it ends the move where the address
  // does not (a redirect), as a navigation's transition ends the lock.
  const movingForRef = useRef<object | null>(null);
  const [moving, sendMove] = useRouterTransition(routerOut);
  const [, sendAside] = useRouterTransition(routerOut);
  const aside = useCallback((what: string, call: () => void) => { sendAside(what, call); }, [sendAside]);
  const [areaWaiting, setAreaWaiting] = useState<AreaWait["waiting"]>(null);

  // A redraw asked for while a move is out (Issue 819): a navigation or a
  // card holding the lock, or a move within the page. Sent then, it is one
  // more action in the App Router's queue beside the move, asking the server
  // for the page the move is going to before the move has drawn it; and in
  // development, where every router action shares one transition, the move
  // commits only with it — a first search's page, asking again each time it
  // heard of a change, kept 我的资料 from opening for as long as it kept
  // asking (34 seconds, in Chrome). So it is owed instead, and sent once the
  // move is over, however many times it was asked for meanwhile.
  //
  // Sent wherever the move ended, the page it left or the page it reached:
  // what was asked for was asked after the move's request went out, so the
  // page it reached may have been drawn before the change the redraw is for
  // — a first search's cards, what the conversation just wrote — and nothing
  // else would draw it again. A document that went away with it (a full
  // load) owes nothing; one brought back from the back-forward cache is let
  // go of (`useRestored`) and pays it there; a move to a page under another
  // shell (the provider unmounted, below) hands it to the router.
  //
  // A write holding the lock is not a move: a redraw asked for beside it
  // goes at once, as it always did.
  const owedRef = useRef(false);
  const moveOut = useCallback(() => (pendingRef.current && !writeRef.current) || areaMoveRef.current !== null, []);
  const redraw = useCallback(() => {
    if (moveOut()) {
      owedRef.current = true;
      return;
    }
    aside("the refresh", () => router.refresh());
  }, [aside, moveOut, router]);
  // A task after the call that ended a move, not sooner. A move replaced
  // (`release("replaced")`, `endAreaMove("replaced")`) is followed in the
  // same call by the one replacing it, which is out in turn. And a lock let
  // go in an event the App Router answers too — `pageshow` from the
  // back-forward cache, where its listener comes after this shell's and
  // restores its state (ACTION_RESTORE), discarding whatever action it finds
  // running — would have the redraw discarded with it: a microtask runs
  // between two listeners of one event, a task only after all of them.
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleOwed = useCallback(() => {
    if (settleTimerRef.current !== null) return;
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      if (!owedRef.current || moveOut()) return;
      owedRef.current = false;
      aside("the refresh", () => router.refresh());
    }, 0);
  }, [aside, moveOut, router]);
  const routerRef = useRef(router);
  routerRef.current = router;

  const clearTimers = useCallback(() => {
    if (showTimerRef.current !== null) clearTimeout(showTimerRef.current);
    if (releaseTimerRef.current !== null) clearTimeout(releaseTimerRef.current);
    if (slowTimerRef.current !== null) clearTimeout(slowTimerRef.current);
    showTimerRef.current = null;
    releaseTimerRef.current = null;
    slowTimerRef.current = null;
  }, []);

  /** The move within the page is over: arrived, given up on, or made part
   *  of a navigation that took the lock ("replaced"). A failure is said by
   *  the first of its areas still on the page, on the page it left them on. */
  const endAreaMove = useCallback((reason: "arrived" | "failed" | "offline" | "replaced") => {
    const move = areaMoveRef.current;
    if (!move) return;
    areaMoveRef.current = null;
    for (const timer of areaTimersRef.current) clearTimeout(timer);
    areaTimersRef.current = [];
    setAreaWaiting(null);
    if (reason === "failed" || reason === "offline") {
      // For the browser's record, if they agreed to it (#788 PR2).
      record("client_error", { surface: "nav", kind: "nav", code: `area:${reason}` });
      console.warn("[navigation] did not open", { to: move.href, reason, areas: move.areas });
      const area = move.areas.find((a) => areasOnPageRef.current.has(a)) ?? null;
      setNotice({ reason, at: targetKey(window.location.href), area });
    }
    settleOwed();
  }, [settleOwed]);

  const release = useCallback((reason: ReleaseReason = "released") => {
    const following = followingRef.current;
    if (reason === "failed" || reason === "offline") {
      record("client_error", { surface: "nav", kind: "nav", code: reason });
      console.warn("[navigation] did not open", {
        to: following?.href ?? cardTargetRef.current,
        reason,
        waitedMs: following ? Date.now() - following.since : null,
      });
      // The page they are on is the browser's address, which the App Router
      // writes only when a page commits. Not `currentLocationRef`: it is
      // written as the provider renders, and the navigation given up on is
      // still rendering its page in a transition, under the target's address.
      if (!quietRef.current) setNotice({ reason, at: targetKey(window.location.href), area: null });
    }
    pendingRef.current = false;
    startedLocationRef.current = null;
    cardTargetRef.current = null;
    followingRef.current = null;
    quietRef.current = false;
    writeRef.current = false;
    clearTimers();
    setVisible(false);
    setPending(false);
    setQuiet(false);
    setSlow(false);
    for (const listener of [...releaseListenersRef.current]) listener(reason);
    releaseListenersRef.current.clear();
    settleOwed();
  }, [clearTimers, settleOwed]);

  const onRelease = useCallback((listener: (reason: ReleaseReason) => void) => {
    releaseListenersRef.current.add(listener);
    return () => releaseListenersRef.current.delete(listener);
  }, []);

  const claim = useCallback((href: string | null, reload: boolean, silent: boolean, holder: Holder): Claim => {
    const move = holder === "write" ? null : holder;
    const target = targetKey(href);
    if (target === null) return "not-local";
    if (pendingRef.current) {
      // A new move can supersede an unfinished card. This matters for an
      // ordinary page link too: a quiet card left it visibly pressable, so
      // refusing it would make the press appear broken. Writes cannot cut in.
      if (!move || cardTargetRef.current === null || cardTargetRef.current === target) return "duplicate";
      release("replaced");
    }
    if (!reload && target === currentLocation) return "same-location";
    pendingRef.current = true;
    claimRef.current++;
    startedLocationRef.current = currentLocation;
    cardTargetRef.current = move === "card" ? target : null;
    quietRef.current = silent;
    writeRef.current = holder === "write";
    // A navigation makes the move within the page its own (see above).
    if (move) endAreaMove("replaced");
    setPending(true);
    setVisible(false);
    setQuiet(silent);
    setNotice(null);
    // Nothing to time when nothing will be drawn. (Not a correctness guard:
    // every claim sets `visible` false above, so the timer firing under a
    // quiet claim would change nothing either way.)
    if (!silent) {
      showTimerRef.current = setTimeout(() => {
        showTimerRef.current = null;
        setVisible(true);
      }, SHOW_AFTER_MS);
    }
    releaseTimerRef.current = setTimeout(() => {
      release(move === "card" ? (window.navigator.onLine ? "failed" : "offline") : "released");
    }, RELEASE_AFTER_MS);
    return "started";
  }, [currentLocation, endAreaMove, release]);

  useEffect(() => {
    const landed = areaMoveRef.current !== null && areaMoveRef.current.target === currentLocation;
    if (landed) endAreaMove("arrived");
    if (!pendingRef.current || startedLocationRef.current === currentLocation) return;
    if (cardTargetRef.current !== null) return;
    if (landed && writeRef.current) {
      // A move within the page arrived while a write was out: the write's
      // page is still here, and its refresh is still to come, after the
      // move (the App Router's queue). It lets go when that commits.
      startedLocationRef.current = currentLocation;
      return;
    }
    const arrived = arrivingForRef.current === claimRef.current;
    arrivingForRef.current = null;
    if (arrived) {
      // A traverse has landed: the lock is now the refresh's, and goes when
      // that commits (the `navigating` effect below).
      navigatingForRef.current = claimRef.current;
      // Refused, nothing will commit to let go: let go now. The page is the
      // one they went back to, only not refreshed — nothing to say on it.
      // Sent, it is also the redraw owed: asked for before this one went.
      if (sendNavigation("the refresh", () => router.refresh())) {
        behindStaleRef.current = false;
        owedRef.current = false;
      } else release();
      return;
    }
    release("arrived");
  }, [currentLocation, endAreaMove, release, router, sendNavigation]);

  // Said on the page it was given up on, and gone for good once they are on
  // another — not kept to come back with them on the browser's back button,
  // which claims nothing.
  useEffect(() => {
    setNotice((was) => (was && was.at !== currentLocation ? null : was));
  }, [currentLocation]);

  useEffect(() => () => {
    pendingRef.current = false;
    clearTimers();
    for (const timer of areaTimersRef.current) clearTimeout(timer);
    for (const listener of [...releaseListenersRef.current]) listener("released");
    releaseListenersRef.current.clear();
    // A redraw owed when the shell goes: the move went to a page under
    // another layout's shell (onboarding's last step into the portal), and
    // that page is drawn again all the same — by the router, as `useRefresh`
    // does with no shell, once the page that replaced this one is in.
    if (settleTimerRef.current !== null) clearTimeout(settleTimerRef.current);
    if (owedRef.current) {
      owedRef.current = false;
      const router = routerRef.current;
      setTimeout(() => router.refresh(), 0);
    }
  }, [clearTimers]);

  // A page brought back from the back-forward cache comes back as it was
  // left, lock and all (Issue 569). What held it is over: the navigation that
  // took them away — a hard one (`NavigationAnchor`, a GET `NavigationForm`),
  // or a soft one the App Router turned into a full load — or a write in
  // flight when they pressed the browser's back. Nothing will let go of it
  // here, so the sheet would stand over the page until the fallback. This is
  // the fallback's `release`, only sooner; and done in this event rather than
  // on a later render, so the restored page is not first drawn with the sheet
  // over it. (The App Router's own `pageshow` resets its state on the same
  // signal.)
  // A move within the page is over the same way: its areas are not left
  // waiting on a request of a page that was left.
  useRestored(() => {
    if (pendingRef.current) flushSync(release);
    if (areaMoveRef.current) flushSync(() => endAreaMove("replaced"));
  });

  const renew = useCallback(() => {
    if (!pendingRef.current) return;
    if (releaseTimerRef.current !== null) clearTimeout(releaseTimerRef.current);
    releaseTimerRef.current = setTimeout(release, RELEASE_AFTER_MS);
  }, [release]);

  const isHere = useCallback((href: string | null) => targetKey(href) === currentLocation, [currentLocation]);

  const go = useCallback((href: string, opts: { replace?: boolean; scroll?: boolean } = {}) => {
    const owner = pendingRef.current ? claimRef.current : null;
    // A write that moves on (`moveTo`) is a navigation from here.
    writeRef.current = false;
    endAreaMove("replaced");
    if (owner !== null) {
      navigatingForRef.current = owner;
      // The navigation's end lets go from here (the `navigating` effect, the
      // address changing); the fallback made for what cannot be seen ending
      // would cut a slow page off before it arrives.
      followingRef.current = { href, since: Date.now() };
      if (releaseTimerRef.current !== null) clearTimeout(releaseTimerRef.current);
      slowTimerRef.current = setTimeout(() => {
        slowTimerRef.current = null;
        setSlow(true);
      }, SLOW_AFTER_MS);
      releaseTimerRef.current = setTimeout(() => {
        release(window.navigator.onLine ? "failed" : "offline");
      }, GIVE_UP_AFTER_MS);
    }
    // Before a request starts we can keep the page and explain the failure.
    // Once Next owns an RSC request, a network failure can make it fall back
    // to a full browser navigation. Releasing on the offline event would only
    // flash this local alert before the browser shows its offline page.
    if (cardTargetRef.current !== null && !window.navigator.onLine) {
      release("offline");
      return;
    }
    const how = opts.scroll === undefined ? undefined : { scroll: opts.scroll };
    // App Router keeps this transition pending until the new page's server
    // payload commits, redirects and all — as it does a refresh's
    // (usePendingRefresh).
    const sent = sendNavigation(href, () => {
      if (opts.replace) router.replace(href, how);
      else router.push(href, how);
    });
    // Refused, nothing is on its way to end the wait: let go now, saying so
    // where the wait was drawn (a card's link, or the page).
    if (!sent && pendingRef.current && claimRef.current === owner) release("failed");
  }, [endAreaMove, release, router, sendNavigation]);

  const moveIn = useCallback((href: string, areas: readonly string[], replace = false): AreaMoveClaim => {
    const url = new URL(href, window.location.href);
    const here = new URL(window.location.href);
    // Only a move that keeps them on this page, and only drawn by areas that
    // are on it: anything else — a link given `redraws` on a page that draws
    // none of them — goes the lock's way, with the sheet, rather than wait
    // with nothing on the screen saying so.
    const drawn = areas.filter((a) => areasOnPageRef.current.has(a));
    if (url.origin !== here.origin || targetKey(url.pathname) !== targetKey(here.pathname) || !drawn.length) return "not-area";
    if (pendingRef.current) {
      if (!quietRef.current) return "refused";
      if (!writeRef.current) {
        if (cardTargetRef.current === null) return "refused";
        release("replaced");
      }
    }
    const was = areaMoveRef.current;
    if (was && drawn.some((a) => was.areas.includes(a))) return "refused";
    const to = was ? composed(was.href, url.href) : local(url.href);
    const target = targetKey(to);
    if (target === (was ? was.target : currentLocation)) return "refused";
    setNotice(null);
    // Before a request starts the page can be kept and the failure said in
    // its area; once Next owns the request, a lost connection is a full
    // page load (as a card's, in `go`).
    if (!window.navigator.onLine) {
      console.warn("[navigation] did not open", { to, reason: "offline", areas: drawn });
      setNotice({ reason: "offline", at: targetKey(window.location.href), area: drawn[0] });
      return "refused";
    }
    const move = { href: to, target, areas: was ? [...was.areas, ...drawn] : drawn };
    areaMoveRef.current = move;
    for (const timer of areaTimersRef.current) clearTimeout(timer);
    // Drawn after the sheet's delay, so a quick move does not flash; a move
    // joined to one already drawn stays drawn.
    setAreaWaiting((w) => ({ areas: move.areas, visible: was !== null && w?.visible === true, slow: false }));
    const later = (ms: number, then: () => void) => setTimeout(() => {
      if (areaMoveRef.current === move) then();
    }, ms);
    areaTimersRef.current = [
      later(SHOW_AFTER_MS, () => setAreaWaiting((w) => w && { ...w, visible: true })),
      later(SLOW_AFTER_MS, () => setAreaWaiting((w) => w && { ...w, slow: true })),
      later(GIVE_UP_AFTER_MS, () => endAreaMove(window.navigator.onLine ? "failed" : "offline")),
    ];
    movingForRef.current = move;
    // In a transition, as `go`'s: the App Router holds it until the page's
    // payload commits. Not scrolled: the page is where it was, some of it
    // redrawn.
    const sent = sendMove(to, () => {
      if (replace) router.replace(to, { scroll: false });
      else router.push(to, { scroll: false });
    });
    if (!sent) {
      endAreaMove("failed");
      return "refused";
    }
    return "started";
  }, [currentLocation, endAreaMove, release, router, sendMove]);

  // The move's transition committed where its address did not arrive (a
  // redirect): it is over all the same.
  useEffect(() => {
    if (moving) return;
    const move = movingForRef.current;
    movingForRef.current = null;
    if (move !== null && areaMoveRef.current === move) endAreaMove("arrived");
  }, [moving, endAreaMove]);

  // An address the page already has everything for (Issue 766): the
  // interviews page's day, which marks the calendar and filters the lists
  // from the month the page came with. The page draws it from the address
  // (`useSearchParams`), so the address is all there is to change, and it is
  // changed through the App Router: once hydrated it patches
  // `history.pushState` to copy its own state — the tree the page was drawn
  // from — into the new entry and restore that tree under the new address
  // (ACTION_RESTORE). So the router's address is the browser's, a reload or a
  // copied link asks the server for that day, and the browser's back and
  // forward go between the days with no request, as the App Router goes
  // back to any page it has. Not #568's way of writing the address behind
  // the router: that was the unpatched call on a full load, before the
  // router had its state, and an entry rewritten on a page brought back.
  // This is only ever a press, on a page hydrated — and only once the
  // router has patched `pushState`: before its effect has run, the call
  // would be the browser's own, behind the router, and the press is an
  // ordinary move instead.
  //
  // Not while the shell has anything on its way — the lock held, a move
  // within the page, or any router call it has sent whose transition has not
  // ended (a navigation, a move, a write's refresh past the lock's fallback
  // or answered after it, a redraw sent with `useRefresh`, `aside`): a restore takes the App Router's queue
  // as a way back through history would, and drops the action it is running
  // for good — a refresh dropped leaves the page as it was before the write,
  // a move dropped leaves its areas waiting to the give-up. The caller makes
  // it an ordinary move instead, which waits its turn by the rules above.
  const showHere = useCallback((href: string): ShowHere => {
    const url = new URL(href, window.location.href);
    const here = new URL(window.location.href);
    if (url.origin !== here.origin || targetKey(url.pathname) !== targetKey(here.pathname)) return "not-here";
    if (window.history.pushState === Object.getPrototypeOf(window.history).pushState) return "not-here";
    if (pendingRef.current || areaMoveRef.current || routerOut.size > 0) return "busy";
    if (targetKey(url.href) === targetKey(here.href)) return "shown";
    window.history.pushState(null, "", local(url.href));
    return "shown";
  }, [routerOut]);

  const address = useCallback(() => {
    const move = areaMoveRef.current;
    return move ? new URL(move.href, window.location.href).href : window.location.href;
  }, []);

  const register = useCallback((area: string) => {
    const on = areasOnPageRef.current;
    on.set(area, (on.get(area) ?? 0) + 1);
    return () => {
      const n = (on.get(area) ?? 1) - 1;
      if (n > 0) on.set(area, n);
      else on.delete(area);
    };
  }, []);

  const traverse = useCallback((delta: number) => {
    if (pendingRef.current) arrivingForRef.current = claimRef.current;
    window.history.go(delta);
  }, []);

  const movedAfterWrite = useCallback(() => {
    behindStaleRef.current = true;
  }, []);
  const refreshed = useCallback(() => {
    behindStaleRef.current = false;
  }, []);

  // The browser's own back and forward (and `router.back()`, a card's
  // close): nothing claims them, and the App Router draws the page they
  // land on from its cache. That is right unless a write since was answered
  // by a move — the rewrite a card started, onboarding's next step — when
  // the page behind was drawn before the write and would show it undone.
  // Then the landing is a traverse's: claimed here, while the provider
  // still has the page being left, and refreshed on arrival by the effect
  // above (Issue 742). Otherwise nothing: closing a card is not a reason to
  // draw the page beneath it again (Issue 728). A lock already held is
  // `claim`'s to decide, as for any navigation: refused, unless it is a card
  // still opening, which the way back replaces. Nor is an entry claimed that
  // no arrival would come to let go: this same page (a hash), or one the
  // router did not make (no `state` — the App Router does nothing with it).
  //
  // Conservative about what is stale: the mark goes with the shell's own
  // refreshes, not with those made straight on the router (the conversation,
  // a wait's redraw) nor with a page drawn fresh by a link since. Next says
  // nothing of which entries its cache holds from when, so at most one way
  // back after such a write is refreshed that did not need it.
  //
  // In the capture phase, and added once. The App Router's own `popstate`
  // listener draws the page it lands on before it returns — the update
  // flushes, effects and all, inside the event — so a listener after it
  // finds the provider already on the new page; and this effect, keyed on a
  // `claim` that changed with the page, has been taken off mid-dispatch, and
  // one taken off is not called (found in Chrome; the first version of this
  // was). At the window a capturing listener runs before every other, and
  // reads `claim` through a ref, so it is never swapped.
  const claimNowRef = useRef(claim);
  claimNowRef.current = claim;
  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      if (!behindStaleRef.current || event.state == null) return;
      const to = targetKey(window.location.href);
      if (to === null || to === currentLocationRef.current) return;
      if (claimNowRef.current(window.location.href, true, false, "navigation") === "started") arrivingForRef.current = claimRef.current;
    };
    window.addEventListener("popstate", onPop, { capture: true });
    return () => window.removeEventListener("popstate", onPop, { capture: true });
  }, []);

  useEffect(() => {
    if (navigating) return;
    const owner = navigatingForRef.current;
    if (owner === null) return;
    navigatingForRef.current = null;
    if (pendingRef.current && claimRef.current === owner) {
      const target = cardTargetRef.current;
      release(target === null || target === currentLocationRef.current ? "arrived" : "failed");
    }
  }, [navigating, release]);

  const s = t(lang);
  const controller = useMemo(
    () => ({ lang, moveIn, showHere, aside, redraw, address, claim, isHere, release, renew, onRelease, go, traverse, movedAfterWrite, refreshed }),
    [lang, moveIn, showHere, aside, redraw, address, claim, go, isHere, onRelease, release, renew, traverse, movedAfterWrite, refreshed],
  );
  const areaWait = useMemo(
    () => ({ lang, waiting: areaWaiting, notice: shownNotice?.area ? shownNotice : null, register }),
    [lang, areaWaiting, shownNotice, register],
  );
  const pageNotice = shownNotice && !shownNotice.area ? shownNotice : null;
  return (
    <RouterOutContext.Provider value={routerOut}>
    <NavigationPendingContext.Provider value={controller}>
      <AreaWaitContext.Provider value={areaWait}>
      {/* Busy is the lock's: a move within the page says so on its areas. */}
      <div className="navigation-pending-root" aria-busy={pending}>
        {children}
        {/* Over an open card, or any modal dialog, these are drawn inside
            it (`OnTop`, Issue 705): on the page they would be under it, the
            sheet stopping nothing and the notice's × out of reach. */}
        {pending && !quiet ? (
          <OnTop>
            <div className="navigation-blocker" data-visible={visible} aria-hidden={!visible}>
              {visible ? (
                <div className="navigation-status" role="status" aria-live="polite" aria-atomic="true">
                  <svg className="navigation-mark" viewBox="0 0 100 100" aria-hidden="true">
                    <path d="M66.6 25.5 A32 32 0 1 0 66.6 74.5" stroke="var(--brand-arc)" strokeWidth="14" strokeLinecap="round" fill="none" />
                    <circle className="navigation-wave" cx="50" cy="50" r="29" fill="none" stroke="var(--brand-arc)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
                    <circle className="navigation-wave-origin" cx="50" cy="50" r="2.4" fill="var(--brand-arc)" />
                    <circle className="navigation-wave-target" cx="79" cy="50" r="9.5" fill="var(--brand-dot)" />
                  </svg>
                  <span>{slow ? s["nav.slow"] : s["nav.loading"]}</span>
                </div>
              ) : null}
            </div>
          </OnTop>
        ) : null}
        {pageNotice ? (
          <OnTop>
            <div className="navigation-failed" role="alert">
              <span>{pageNotice.reason === "offline" ? s["nav.offline"] : s["nav.failed"]}</span>
              <button type="button" aria-label={s["nav.dismiss"]} data-track="nav.dismiss_notice" onClick={() => setNotice(null)}>×</button>
            </div>
          </OnTop>
        ) : null}
      </div>
      </AreaWaitContext.Provider>
    </NavigationPendingContext.Provider>
    </RouterOutContext.Provider>
  );
}

/**
 * What an area of the page draws of a move within the page that redraws it
 * (`redraws`, Issue 713): whether it is waiting — `visible` after the
 * sheet's delay, `slow` past `SLOW_AFTER_MS` — and a failure it is the one
 * to say. Being drawn is what lets a link wait in it at all: an area that is
 * not on the page cannot say it is waiting, and the move goes the lock's way.
 */
export function useLoadingArea(name: string): Readonly<{
  lang: Lang | null; busy: boolean; visible: boolean; slow: boolean; failed: Notice["reason"] | null;
}> {
  const wait = useContext(AreaWaitContext);
  const register = wait?.register;
  useEffect(() => register?.(name), [register, name]);
  const busy = wait?.waiting?.areas.includes(name) ?? false;
  return {
    lang: wait?.lang ?? null,
    busy,
    visible: busy && wait!.waiting!.visible,
    slow: busy && wait!.waiting!.slow,
    failed: wait?.notice?.area === name ? wait.notice.reason : null,
  };
}

/**
 * A move to a page of this app decided in code — after a write that says
 * where to go, such as a rewrite's page once it is begun (#233) — inside the
 * shell's one lock, as a NavigationLink's press would be — a card's own
 * address included, which opens the card over this page. The caller releases
 * any refresh it holds first: there is one lock.
 *
 * `redraws` is a NavigationLink's: a move that only changes this page's
 * query, made from a control that is not a link — the pager's rows-per-page
 * and its page box (#880) — waits in those areas and leaves the rest of the
 * page free; one the page cannot draw there takes the lock, as a link does.
 * A move refused there (an area already waiting, a navigation out) is no move.
 */
export function useNavigate(): (href: string, opts?: { redraws?: readonly string[] }) => void {
  const controller = useContext(NavigationPendingContext);
  const router = useRouter();
  return useCallback((href: string, opts?: { redraws?: readonly string[] }) => {
    if (controller && typeof window !== "undefined") {
      if (opts?.redraws?.length && controller.moveIn(href, opts.redraws) !== "not-area") return;
      const to = softTarget(href, controller.address());
      const claim = controller.claim(to.href, false, to.card, to.card ? "card" : "navigation");
      if (claim === "duplicate" || claim === "same-location") return;
      if (claim === "started") return controller.go(local(to.href), to.card ? { scroll: false } : undefined);
    }
    router.push(href);
  }, [controller, router]);
}

type NavigationLinkProps = ComponentProps<typeof Link> & {
  /** The areas of this page the move redraws (`LoadingArea`), for a link that
   *  only changes the page's query: its wait is drawn there and the rest of
   *  the page is left free (Issue 713). Left out, the move is the page's. */
  redraws?: readonly string[];
  /** The page already has what this link shows, and draws it from the
   *  address (Issue 766): pressed, only the address changes (`showHere`), and
   *  the server is not asked — so it is not prefetched either. When the shell
   *  is busy it is an ordinary move, waiting in its `redraws`. It adds an
   *  entry; a link that `replace`s is an ordinary move. */
  here?: boolean;
};

/**
 * What a link to a card's own address is drawn with. Its address spelled one
 * way (`ownHref`): `/jobs/greenhouse:p_1` and `/jobs/greenhouse%3Ap_1` are
 * one page to the server and two to the App Router, and with both on a page
 * its prefetch of the one it had not written itself went round forever —
 * hundreds of requests a second to the same posting (Issue 570, which Issue
 * 567 made live). And no prefetch: a press opens the card over this page, a
 * request of its own, so a prefetch of the card's page is never used.
 * Anything else — another page, another site, `//host/…` — is left as written.
 */
export function cardLink(href: NavigationLinkProps["href"], prefetch: NavigationLinkProps["prefetch"]): Pick<NavigationLinkProps, "href" | "prefetch"> {
  if (typeof href !== "string" || !href.startsWith("/") || href.startsWith("//")) return { href, prefetch };
  const url = new URL(href, "http://x");
  const card = cardAt(url.pathname);
  return card ? { href: ownHref(card) + url.search + url.hash, prefetch: false } : { href, prefetch };
}

/** A Next Link that participates in the shell-wide synchronous lock. The
 *  navigation itself is the shell's (`go`), not Link's own, so the lock can
 *  let go when it lands even where the address ends up unchanged; Link still
 *  draws the anchor, prefetches, and leaves modified clicks, new windows and
 *  other sites to the browser. What Link's own dispatch carried and `go`
 *  does not: the link as the current navigation (`useLinkStatus` stays idle
 *  for these links), its `transitionTypes`, and the prefetch intent the
 *  `onRouterTransitionStart` hook reads. Nothing here uses any of them; a
 *  caller that comes to need one passes it through `go`.
 *
 *  A link to a card's own page — `/jobs/<key>`, `/companies/<slug>`,
 *  `/reports/<id>` — opens that card over the page they are on
 *  (`softTarget`). The anchor keeps the card's own address, which is what a
 *  new tab, a copied link or a hover shows — spelled one way, and not
 *  prefetched (`cardLink`). */
export function NavigationLink({ onNavigate, ref: forwardedRef, redraws, here, ...props }: NavigationLinkProps) {
  const controller = useContext(NavigationPendingContext);
  const [cardWaiting, setCardWaiting] = useState(false);
  const [cardFailure, setCardFailure] = useState<"failed" | "offline" | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const anchorRef = useRef<HTMLAnchorElement | null>(null);
  const attachRef = useCallback((node: HTMLAnchorElement | null) => {
    anchorRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) (forwardedRef as MutableRefObject<HTMLAnchorElement | null>).current = node;
  }, [forwardedRef]);
  useEffect(() => () => unsubscribeRef.current?.(), []);
  const drawn = cardLink(props.href, here ? false : props.prefetch);
  if (!controller) return <Link ref={attachRef} onNavigate={onNavigate} {...props} {...drawn} />;
  const s = t(controller.lang);
  return (
    <Link
      {...props}
      {...drawn}
      ref={attachRef}
      aria-busy={cardWaiting || props["aria-busy"]}
      aria-disabled={cardWaiting || props["aria-disabled"]}
      data-card-waiting={cardWaiting ? "" : undefined}
      onNavigate={(event) => {
        // A move within the page (`here`, `redraws`) asks the consumer
        // first: once it is sent there is nothing to take back if they cancel.
        let ask = onNavigate;
        if ((here || redraws?.length) && anchorRef.current) {
          let cancelled = false;
          ask?.({ preventDefault() { cancelled = true; event.preventDefault(); } });
          if (cancelled) return;
          ask = undefined;
          if (here && !props.replace && controller.showHere(anchorRef.current.href) === "shown") {
            event.preventDefault();
            return;
          }
        }
        if (redraws?.length && anchorRef.current) {
          if (controller.moveIn(anchorRef.current.href, redraws, props.replace) !== "not-area") {
            event.preventDefault();
            return;
          }
        }
        const to = anchorRef.current ? softTarget(anchorRef.current.href, controller.address()) : null;
        const claim = controller.claim(to?.href ?? null, false, to?.card ?? false, to?.card ? "card" : "navigation");
        if (claim === "duplicate") {
          event.preventDefault();
          return;
        }
        if (claim === "same-location") {
          event.preventDefault();
          return;
        }
        if (claim === "not-local") return;
        if (to?.card) {
          unsubscribeRef.current?.();
          setCardWaiting(true);
          setCardFailure(null);
          unsubscribeRef.current = controller.onRelease((reason) => {
            unsubscribeRef.current?.();
            unsubscribeRef.current = null;
            setCardWaiting(false);
            if (reason === "failed" || reason === "offline") setCardFailure(reason);
          });
        }
        let prevented = false;
        try {
          ask?.({
            preventDefault() {
              prevented = true;
              event.preventDefault();
            },
          });
        } catch (error) {
          if (claim === "started") controller.release();
          throw error;
        }
        if (prevented) {
          if (claim === "started") controller.release();
          return;
        }
        event.preventDefault();
        // A card opens over the page without moving it (`softTarget`).
        controller.go(local(to!.href), { replace: props.replace, scroll: to!.card ? false : props.scroll ?? undefined });
      }}
    >
      {props.children}
      {cardFailure ? <span className="card-nav-failed" role="alert">{cardFailure === "offline" ? s["nav.offline"] : s["nav.cardFailed"]}</span> : null}
    </Link>
  );
}

function plainSelfNavigation(event: MouseEvent<HTMLAnchorElement>): boolean {
  const a = event.currentTarget;
  return event.button === 0
    && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
    && (!a.target || a.target === "_self")
    && !a.hasAttribute("download");
}

type NavigationAnchorProps = ComponentProps<"a">;

/** A deliberate hard navigation that still joins the shell's one lock. */
export function NavigationAnchor({ onClick, ...props }: NavigationAnchorProps) {
  const controller = useContext(NavigationPendingContext);
  return (
    <a
      {...props}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || !controller || !plainSelfNavigation(event)) return;
        const claim = controller.claim(event.currentTarget.href, false, false, "navigation");
        if (claim === "duplicate" || claim === "same-location") event.preventDefault();
      }}
    />
  );
}

type NavigationTraverseProps = Omit<ComponentProps<"a">, "href"> & {
  /** The entry it goes back to: its address, drawn as the link's `href`, and
   *  how far back it is (`shell/history.ts leaving`). */
  to: { href: string; delta: number };
};

/** A way back through the browser's history that joins the shell's one lock
 *  (`traverse`). The anchor carries the entry's address, so a modified click
 *  or a new window opens that page as any link would. */
export function NavigationTraverse({ to, onClick, ...props }: NavigationTraverseProps) {
  const controller = useContext(NavigationPendingContext);
  return (
    <a
      {...props}
      href={to.href}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || !plainSelfNavigation(event)) return;
        event.preventDefault();
        if (!controller) {
          window.history.go(to.delta);
          return;
        }
        if (controller.claim(event.currentTarget.href, false, false, "navigation") === "started") controller.traverse(to.delta);
      }}
    />
  );
}

function formTarget(form: HTMLFormElement, submitter: HTMLElement | null): string | null {
  // tagName is realm-independent: instanceof fails when a form or submitter
  // comes from another Window (embedded documents and DOM test harnesses).
  const submitterTag = submitter?.tagName;
  const control = submitterTag === "BUTTON" || submitterTag === "INPUT"
    ? submitter as HTMLButtonElement | HTMLInputElement
    : null;
  const methodOverride = control?.getAttribute("formmethod") ?? null;
  const targetOverride = control?.getAttribute("formtarget") ?? null;
  const method = methodOverride === null ? form.method : methodOverride || "get";
  const target = targetOverride === null ? form.target : targetOverride;
  if (method.toLowerCase() !== "get" || (target && target.toLowerCase() !== "_self")) return null;
  const data = control
    ? new FormData(form, control)
    : new FormData(form);
  const search = new URLSearchParams();
  for (const [name, value] of data) {
    if (typeof value !== "string") return null;
    search.append(name, value);
  }
  const actionOverride = control?.getAttribute("formaction") ?? null;
  const action = actionOverride === null ? form.action : actionOverride;
  const url = new URL(action || window.location.href, window.location.href);
  url.search = search.toString();
  return url.href;
}

type NavigationFormProps = ComponentProps<"form">;

/** A GET form whose browser navigation participates in the shared lock. */
export function NavigationForm({ onSubmit, ...props }: NavigationFormProps) {
  const controller = useContext(NavigationPendingContext);
  function submit(event: Parameters<NonNullable<NavigationFormProps["onSubmit"]>>[0]) {
    onSubmit?.(event);
    if (event.defaultPrevented || !controller) return;
    const native = event.nativeEvent as SubmitEvent;
    const href = formTarget(event.currentTarget, native.submitter);
    if (href === null) return;
    const claim = controller.claim(href, false, false, "navigation");
    if (claim === "duplicate" || claim === "same-location") event.preventDefault();
  }
  return <form {...props} onSubmit={submit} />;
}

/** Claim the current address before a deliberate full reload. */
export function useNavigationReload(): () => boolean {
  const controller = useContext(NavigationPendingContext);
  return useCallback(() => {
    if (!controller || typeof window === "undefined") return true;
    return controller.claim(window.location.href, true, false, "navigation") === "started";
  }, [controller]);
}

export type PendingRefreshTask = Readonly<{
  refresh: () => Promise<void>;
  /**
   * Go there, still holding the lock, and let go on arrival (#397).
   *
   * The lock is released by the address changing, so a move to the address
   * already being shown would never be released — it is a refresh, and this
   * is the only place that can tell the two apart, because it is the only
   * place that knows the address. A caller that compared what it had drawn
   * instead would be wrong whenever the two disagree, which onboarding's
   * page allows: `?step=` above the progress the server will honour renders
   * the earlier page while the address keeps the later one.
   *
   * In place of this entry by default (onboarding's steps are one page);
   * `replace: false` leaves it behind, to come back to — the card a rewrite
   * was started from. Either way the pages behind were drawn before the
   * write, and the shell refreshes the one the browser's back lands on
   * (`movedAfterWrite`, Issue 742). A write that ends in a move goes through
   * here, not `release()` and a navigation: the lock is not let go between
   * the two.
   */
  moveTo: (href: string, options?: { replace?: boolean }) => Promise<void>;
  release: () => void;
}>;

type PendingRefresh = Readonly<{
  begin: (options?: { quiet?: boolean }) => PendingRefreshTask | null;
}>;

type PendingTask = {
  promise: Promise<void>;
  resolve: () => void;
  unsubscribe: (() => void) | null;
  timer: ReturnType<typeof setTimeout> | null;
  active: boolean;
  refreshStarted: boolean;
};

/**
 * Own one user write from before its request through the refreshed RSC commit.
 *
 * `begin({ quiet })` takes the lock without the sheet over the page (#397):
 * for a write that draws its own waiting state where the write is happening
 * (see the provider's `quiet`). It is said per write, not per hook: one
 * page's buttons can be either (the rewrite page's 全部接受 is quiet, its
 * restart is not). Everything else about it is the same — a second write in
 * the same turn is still refused, and the refresh still ends the ownership.
 */
export function usePendingRefresh(): PendingRefresh {
  const controller = useContext(NavigationPendingContext);
  const router = useRouter();
  const [refreshing, sendRefresh] = useRouterTransition();
  const task = useRef<PendingTask | null>(null);
  const controllerRef = useRef(controller);
  controllerRef.current = controller;

  const complete = useCallback((owner: PendingTask) => {
    if (!owner.active) return;
    owner.active = false;
    owner.refreshStarted = false;
    if (task.current === owner) task.current = null;
    owner.unsubscribe?.();
    if (owner.timer !== null) clearTimeout(owner.timer);
    owner.resolve();
  }, []);

  const release = useCallback((owner: PendingTask) => {
    if (!owner.active) return;
    const currentController = controllerRef.current;
    if (task.current === owner && currentController) currentController.release();
    else complete(owner);
  }, [complete]);

  useEffect(() => {
    const owner = task.current;
    if (!refreshing && owner?.active && owner.refreshStarted) release(owner);
  }, [refreshing, release]);

  useEffect(() => () => {
    const owner = task.current;
    if (owner) release(owner);
  }, [release]);

  const begin = useCallback(({ quiet = false }: { quiet?: boolean } = {}) => {
    if (task.current?.active) return null;
    if (controller && typeof window !== "undefined"
      && controller.claim(window.location.href, true, quiet, "write") !== "started") return null;
    let resolve!: () => void;
    const promise = new Promise<void>((done) => { resolve = done; });
    const owner: PendingTask = {
      promise,
      resolve,
      unsubscribe: null,
      timer: null,
      active: true,
      refreshStarted: false,
    };
    task.current = owner;
    if (controller) owner.unsubscribe = controller.onRelease(() => complete(owner));
    else owner.timer = setTimeout(() => complete(owner), RELEASE_AFTER_MS);
    const refresh = () => {
      // The fallback unlocks the UI; it does not cancel an in-flight POST.
      // A late response may still redraw, but it must never borrow or
      // release a newer operation's ownership token.
      if (!owner.active) {
        // Sent aside, so it is counted out though the lock it was for is gone.
        const shell = controllerRef.current;
        if (shell) shell.aside("the refresh", () => router.refresh());
        else router.refresh();
        shell?.refreshed();
        return owner.promise;
      }
      owner.refreshStarted = true;
      // App Router keeps this transition pending until the refreshed server
      // payload commits. That is the completion signal `router.refresh()`
      // itself does not return. Refused, nothing will commit: let go now,
      // which settles the promise — the write's answer is in all the same.
      if (sendRefresh("the refresh", () => router.refresh())) controllerRef.current?.refreshed();
      else release(owner);
      return owner.promise;
    };
    return {
      refresh,
      moveTo(href: string, { replace = true }: { replace?: boolean } = {}) {
        const move = (to: string) => (replace ? router.replace(to) : router.push(to));
        // Nobody to be held by without a provider: go, and let go now rather
        // than sit out the fallback.
        if (!controller) {
          move(href);
          release(owner);
          return owner.promise;
        }
        if (controller.isHere(href)) return refresh();
        controller.movedAfterWrite();
        // A late answer, the lock let go meanwhile: it is not ours to hand to
        // the move — it may be another's by now. Go, holding nothing.
        if (!owner.active) {
          controller.aside("the move", () => move(href));
          return owner.promise;
        }
        // The shell's own navigation, followed as a link's is (`go`, Issue
        // 697): resolves when the provider lets go — on arrival (the address
        // changing, or the move's transition committing where it did not),
        // or when the move is given up on.
        controller.go(href, { replace });
        return owner.promise;
      },
      release() {
        release(owner);
      },
    };
  }, [complete, controller, release, router, sendRefresh]);

  return useMemo(() => ({ begin }), [begin]);
}

/** One of several writes sharing a lock: the same two verbs as a
 *  `PendingRefreshTask`, so a caller that holds either writes the same code. */
export type PendingWrite = Pick<PendingRefreshTask, "refresh" | "release">;

type Waiter = { upTo: number; resolve: () => void };

/**
 * Several writes at once, under one lock, redrawn as few times as they need
 * (Issue 534): the rewrite page's changes, the home page's proposed rules —
 * blocks that are each their own write, where waiting for one before
 * pressing the next is waiting for nothing. (The server takes them one at a
 * time: `decideChanges` locks the rewrite, `confirm` the row.)
 *
 * The lock is the shell's, taken quietly by the first write and joined by
 * the ones after it while it is held, so everything the lock is for still
 * holds: a navigation, or a write from `usePendingRefresh`, is refused while
 * any of these is out, and none of these starts while one of those is. A
 * second write under the same `key` — the same block pressed twice — is
 * refused (`null`). Each write that joins pushes the fallback release back
 * (`renew`): the eight seconds are for a lock nobody is using any more.
 *
 * `refresh()` says this write has its answer. It resolves once a refresh
 * that *began after that* has committed — so a block stops saying 「正在…」
 * only when the page it is drawn in has heard of its write, never on a
 * redraw that was already on its way when it landed. The redraws are
 * shared, because each `router.refresh()` is one more render of the whole
 * page in the App Router's queue, one after another: at most one is in
 * flight and one more is asked for behind it, however many writes land
 * meanwhile — three quick presses are two renders, not three in a row.
 *
 * The lock is let go when no write is out and no redraw is owed. If it goes
 * first — the fallback, or the page changing — every write waiting on a
 * redraw is let go at once, and one that answers later redraws the page
 * plainly while it is still on it (not the page they went to instead).
 */
export function usePendingWrites(): Readonly<{ begin: (key: string) => PendingWrite | null }> {
  const controller = useContext(NavigationPendingContext);
  const router = useRouter();
  const [refreshing, sendRefresh] = useRouterTransition();
  const controllerRef = useRef(controller);
  controllerRef.current = controller;
  const routerRef = useRef(router);
  routerRef.current = router;
  const batch = useRef({
    /** This hook holds the shell's lock (or there is no shell to hold). */
    held: false,
    /** The writes begun and not yet released. */
    keys: new Set<string>(),
    /** Redraws asked for, numbered from 1, and the newest one a committed
     *  refresh has covered. */
    asked: 0,
    covered: 0,
    /** What the refresh in flight covers — 0 when none is. */
    running: 0,
    waiters: [] as Waiter[],
    unsubscribe: null as (() => void) | null,
    mounted: true,
  });

  const settle = useCallback((all: boolean) => {
    const b = batch.current;
    const still: Waiter[] = [];
    for (const waiter of b.waiters) {
      if (all || waiter.upTo <= b.covered) waiter.resolve();
      else still.push(waiter);
    }
    b.waiters = still;
  }, []);

  const letGo = useCallback(() => {
    const b = batch.current;
    if (!b.held || b.keys.size || b.running || b.waiters.length) return;
    b.held = false;
    b.unsubscribe?.();
    b.unsubscribe = null;
    controllerRef.current?.release();
  }, []);

  // The provider let go without us: the fallback, or the address changed.
  const lost = useCallback(() => {
    const b = batch.current;
    b.held = false;
    b.unsubscribe = null;
    b.running = 0;
    b.covered = b.asked;
    settle(true);
  }, [settle]);

  const pump = useCallback(() => {
    const b = batch.current;
    if (b.running || b.covered >= b.asked) return;
    b.running = b.asked;
    // As in `usePendingRefresh`: the App Router keeps this transition pending
    // until the refreshed payload commits. Refused, nothing will: the writes
    // waiting on it have their answers, and are let go now. The lock is not
    // this one's to let go — both callers do, once it returns: a write asking
    // is still out, and the commit effect lets go after it.
    if (sendRefresh("the refresh", () => routerRef.current.refresh())) {
      controllerRef.current?.refreshed();
      return;
    }
    b.running = 0;
    b.covered = b.asked;
    settle(false);
  }, [sendRefresh, settle]);

  useEffect(() => {
    const b = batch.current;
    if (refreshing || !b.running) return;
    b.covered = b.running;
    b.running = 0;
    settle(false);
    pump();
    letGo();
  }, [refreshing, letGo, pump, settle]);

  useEffect(() => {
    const b = batch.current;
    b.mounted = true;
    return () => {
      b.mounted = false;
      const held = b.held && controllerRef.current !== null;
      b.held = false;
      b.unsubscribe?.();
      b.unsubscribe = null;
      b.running = 0;
      b.covered = b.asked;
      settle(true);
      if (held) controllerRef.current?.release();
    };
  }, [settle]);

  const begin = useCallback((key: string): PendingWrite | null => {
    const b = batch.current;
    if (b.keys.has(key)) return null;
    const shell = controllerRef.current;
    if (b.held) {
      shell?.renew();
    } else {
      if (shell && typeof window !== "undefined") {
        if (shell.claim(window.location.href, true, true, "write") !== "started") return null;
        b.unsubscribe = shell.onRelease(lost);
      }
      b.held = true;
    }
    b.keys.add(key);
    let open = true;
    let redrawn: Promise<void> | null = null;
    return {
      refresh() {
        if (redrawn) return redrawn;
        if (!b.held) {
          // Nothing left to redraw under (see above). One refresh per late
          // answer, not shared: it only happens past the fallback.
          // Sent aside, so it is counted out though the lock is gone.
          if (b.mounted) {
            const shell = controllerRef.current;
            if (shell) shell.aside("the refresh", () => routerRef.current.refresh());
            else routerRef.current.refresh();
            shell?.refreshed();
          }
          redrawn = Promise.resolve();
          return redrawn;
        }
        const upTo = ++b.asked;
        redrawn = new Promise<void>((resolve) => { b.waiters.push({ upTo, resolve }); });
        pump();
        return redrawn;
      },
      release() {
        if (!open) return;
        open = false;
        b.keys.delete(key);
        letGo();
      },
    };
  }, [letGo, lost, pump]);

  return useMemo(() => ({ begin }), [begin]);
}
